import React, { useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";
import Modal from "@mui/material/Modal";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  FormGroup,
  Select,
  MenuItem,
  CircularProgress,
  Typography,
} from "@mui/material";
import userSupportService from "../../../services/UserSupportService";
import { getStyles } from "./styles";

function getModalStyle() {
  return {
    top: `36vh`,
    left: `37.5vw`,
  };
}

const supportedLanguages = ["English", "Spanish"];

type ChangeLanguageModalProps = {
  userAccountId: string;
  userId: string;
  siteId: number;
  applicationLanguage: string;
  open: boolean;
  handleClose: (language: string | undefined) => void;
};

const getLanguageCode = (text: "English" | "Spanish"): "en" | "es" => {
  return text === "English" ? "en" : "es";
};

const getLanguageText = (code: string): "English" | "Spanish" => {
  return code === "en" ? "English" : "Spanish";
};

export default function ChangeLanguageModal(props: ChangeLanguageModalProps) {
  const theme = useTheme();
  const styles = getStyles(theme);
  const [modalStyle] = useState(getModalStyle);
  const [userLanguage, setUserLanguage] = useState<
    "English" | "Spanish" | undefined
  >();
  const [savedLanguage, setSavedLanguage] = useState<'English' | 'Spanish' | undefined>();
  const [isLoading, setLoading] = useState(false);
  const [hasErr, setErr] = useState(false);
  const [disableSend, setDisableSend] = useState(true);
  const [hasSuccess, setSuccess] = useState(false);

  useEffect(() => {
    setUserLanguage(getLanguageText(props.applicationLanguage));
  }, [props.applicationLanguage]);

  const submitForm = async () => {
    setErr(false);
    setSuccess(false);
    setLoading(true);
    const lang = getLanguageCode(userLanguage!);
    const result = await userSupportService.changeUserApplicationLanguage(
      props.userAccountId,
      props.userId,
      props.siteId,
      lang
    );

    if (result) {
      setSavedLanguage(userLanguage);
    }

    setSuccess(!!result);
    setLoading(false);
  };

  const resetState = () => {
    setLoading(false);
    setErr(false);
    setSuccess(false);
    setDisableSend(true);
    setUserLanguage(
      savedLanguage
        ? savedLanguage
        : getLanguageText(props.applicationLanguage)
    );
    setSavedLanguage(undefined);
  };

  const handleLanguageChange = (e: any) => {
    const disabled = savedLanguage
      ? !!(e.target.value === savedLanguage)
      : !!(e.target.value === getLanguageText(props.applicationLanguage));
    setDisableSend(disabled);
    setUserLanguage(e.target.value);
  };

  return (
    <Modal
      aria-labelledby="user-change-language"
      aria-describedby="user-language-modal-description"
      open={props.open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          props.handleClose(savedLanguage);
        }
      }}
    >
      <Box style={modalStyle} sx={styles.languagePaper}>
        <h2 id="user-change-language">Language</h2>
        <p id="user-language-modal-description">
          Use the form below to change the language.
        </p>
        <FormGroup>
          <FormControl variant="standard">
            <InputLabel htmlFor="userLanguage">
              User's application language
            </InputLabel>
            <Select
              variant="standard"
              id="user-change-language-selector"
              labelId="userLanguage"
              value={userLanguage ?? ''}
              onChange={handleLanguageChange}>
              {supportedLanguages.map((lang) => {
                return (
                  <MenuItem key={lang} value={lang} id={"option-" + lang}>
                    {lang}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </FormGroup>
        <FormGroup>
          {isLoading ? <CircularProgress color="primary" /> : undefined}
          {hasSuccess ? (
            <Typography sx={styles.greenTxt} id="success-msg">
              Language changed successfully!
            </Typography>
          ) : undefined}
          {hasErr ? (
            <Typography sx={styles.redTxt} id="error-msg">
              Language not changed successfully!
            </Typography>
          ) : undefined}
        </FormGroup>
        <FormGroup
          sx={[styles.mt2, styles.dfr, styles.stickToBottom]}
        >
          <Button
            variant="contained"
            color="secondary"
            disabled={disableSend}
            onClick={submitForm}
            id="btn-change-language"
          >
            Change Language
          </Button>
          <Button
            variant="contained"
            id="btn-close"
            onClick={() => {
              resetState();
              props.handleClose(savedLanguage ? getLanguageCode(savedLanguage) : undefined);
            }}
            sx={styles.ml2}>
            Close
          </Button>
        </FormGroup>
      </Box>
    </Modal>
  );
}